import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';

const IMAGES = [
  {
    id: 1,
    src: '/10.jpg',
    alt: 'Necklace Sets',
    label: 'Necklace Sets',
    cat: 'Collection',
    pos: 'center top',
  },
  {
    id: 2,
    src: '/2.jpg',
    alt: 'Earrings',
    label: 'Earrings',
    cat: 'Collection',
    pos: 'center top',
  },
  {
    id: 3,
    src: '/4.jpg',
    alt: 'Bangles',
    label: 'Bangles',
    cat: 'Collection',
    pos: 'center',
  },
  {
    id: 4,
    src: 'https://images.pexels.com/photos/265906/pexels-photo-265906.jpeg?auto=compress&cs=tinysrgb&w=600',
    alt: 'Rings',
    label: 'Rings',
    cat: 'Collection',
    pos: 'center top',
  },
  {
    id: 5,
    src: 'https://images.pexels.com/photos/1395306/pexels-photo-1395306.jpeg?auto=compress&cs=tinysrgb&w=600',
    alt: 'Diamond Sets',
    label: 'Diamond Sets',
    cat: 'Collection',
    pos: 'center',
  },
  {
    id: 6,
    src: '/9.jpg',
    alt: 'Bridal Sets',
    label: 'Bridal Sets',
    cat: 'Collection',
    pos: 'center top',
  },
];

const Tile = ({ item, num, delay }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });

  return (
    <motion.div
      ref={ref}
      className="jg-tile"
      initial={{ opacity: 0, y: 24 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay, ease: [0.25, 0.46, 0.45, 0.94] }}
    >
      <img src={item.src} alt={item.alt} style={{ objectPosition: item.pos }} loading="lazy" />
      <div className="jg-shine" />
      <span className="jg-num">{String(num).padStart(2, '0')}</span>
      <div className="jg-bot">
      </div>
      <div className="jg-line" />
    </motion.div>
  );
};

const JewelleryGallery = () => {
  const headRef = useRef(null);
  const headInView = useInView(headRef, { once: true, margin: '-60px' });

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,700;1,400&family=Jost:wght@400;500;600&display=swap');

        .jg {
          background: #FAF8F4;
          padding: 72px clamp(20px, 5vw, 80px) 80px;
          font-family: 'Jost', sans-serif;
        }

        .jg-head { margin-bottom: 48px; }

        .jg-eye {
          font-size: 10px;
          font-weight: 500;
          letter-spacing: 0.3em;
          text-transform: uppercase;
          color: #B08D57;
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 10px;
        }
        .jg-eye::before {
          content: '';
          width: 28px;
          height: 1px;
          background: #B08D57;
        }

        .jg-ttl {
          font-family: 'Playfair Display', serif;
          font-size: clamp(26px, 3.5vw, 40px);
          font-weight: 700;
          color: #1C1A18;
          line-height: 1.08;
          letter-spacing: -0.01em;
        }
        .jg-ttl em {
          font-style: italic;
          font-weight: 400;
          color: #B08D57;
        }

        .jg-sub {
          font-family: 'Playfair Display', serif;
          font-size: 14px;
          font-style: italic;
          color: #8A7E72;
          margin-top: 5px;
        }

        /* ── Grid rows ── */
        .jg-row {
          display: grid;
          gap: 3px;
          margin-bottom: 3px;
        }
        .jg-r1 {
          grid-template-columns: 2fr 1fr 1fr;
          height: 360px;
        }
        .jg-r2 {
          grid-template-columns: 1fr 1fr 2fr;
          height: 260px;
        }

        /* ── Tile ── */
        .jg-tile {
          position: relative;
          overflow: hidden;
          background: #1C1A18;
          cursor: pointer;
        }
        .jg-tile img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
          transition: transform 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94),
                      filter 0.6s ease;
          filter: brightness(0.82);
        }
        .jg-tile:hover img {
          transform: scale(1.08);
          filter: brightness(0.6);
        }

        /* Gold shimmer on hover */
        .jg-shine {
          position: absolute;
          inset: 0;
          background: linear-gradient(
            135deg,
            rgba(212, 175, 120, 0.12) 0%,
            transparent 60%
          );
          opacity: 0;
          transition: opacity 0.4s;
          pointer-events: none;
          z-index: 1;
        }
        .jg-tile:hover .jg-shine { opacity: 1; }

        /* Number badge top-right */
        .jg-num {
          position: absolute;
          top: 12px;
          right: 14px;
          z-index: 2;
          font-size: 9px;
          font-weight: 600;
          letter-spacing: 0.18em;
          color: rgba(250, 248, 244, 0.45);
          transition: color 0.3s;
        }
        .jg-tile:hover .jg-num { color: rgba(212, 175, 120, 0.9); }

        /* Bottom info area */
        .jg-bot {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          padding: 32px 16px 14px;
          background: linear-gradient(to top, rgba(12, 9, 5, 0.85) 0%, transparent 100%);
          z-index: 2;
        }

        .jg-cat {
          font-size: 8.5px;
          font-weight: 600;
          letter-spacing: 0.26em;
          text-transform: uppercase;
          color: rgba(212, 175, 120, 0.85);
          display: block;
          margin-bottom: 4px;
        }

        /* Product name slides up on hover */
        .jg-nm {
          font-family: 'Playfair Display', serif;
          font-size: clamp(13px, 1.6vw, 18px);
          font-weight: 700;
          color: #FAF8F4;
          line-height: 1.15;
          letter-spacing: -0.01em;
          margin: 0;
          opacity: 0;
          transform: translateY(8px);
          transition: opacity 0.35s 0.05s ease, transform 0.35s 0.05s ease;
        }
        .jg-tile:hover .jg-nm {
          opacity: 1;
          transform: translateY(0);
        }

        /* Gold underline sweeps left to right */
        .jg-line {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 2px;
          background: #B08D57;
          transform: scaleX(0);
          transform-origin: left;
          transition: transform 0.45s cubic-bezier(0.25, 0.46, 0.45, 0.94);
          z-index: 3;
        }
        .jg-tile:hover .jg-line { transform: scaleX(1); }

        /* ── Ornament ── */
        .jg-orn {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 14px;
          margin-top: 44px;
          opacity: 0.4;
        }
        .jg-orn::before, .jg-orn::after { content: ''; width: 70px; height: 1px; }
        .jg-orn::before { background: linear-gradient(to right, transparent, #B08D57); }
        .jg-orn::after  { background: linear-gradient(to left, transparent, #B08D57); }
        .jg-dia {
          width: 5px;
          height: 5px;
          background: #B08D57;
          transform: rotate(45deg);
        }

        /* ── Responsive ── */
        @media (max-width: 640px) {
          .jg-r1, .jg-r2 {
            grid-template-columns: 1fr 1fr;
            height: auto;
          }
          .jg-r1 .jg-tile:first-child,
          .jg-r2 .jg-tile:last-child {
            grid-column: 1 / -1;
            height: 240px;
          }
          .jg-r1 .jg-tile:not(:first-child),
          .jg-r2 .jg-tile:not(:last-child) {
            height: 180px;
          }
        }
      `}</style>

      <section className="jg">

        {/* Header */}
        <div className="jg-head" ref={headRef}>
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={headInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <p className="jg-eye">Our Craft</p>
            <h2 className="jg-ttl">A Glimpse of <em>Elegance</em></h2>
            <p className="jg-sub">Each piece, a whisper of artistry worn close to the heart.</p>
          </motion.div>
        </div>

        {/* Row 1 — wide left */}
        <div className="jg-row jg-r1">
          <Tile item={IMAGES[0]} num={1} delay={0.05} />
          <Tile item={IMAGES[1]} num={2} delay={0.15} />
          <Tile item={IMAGES[2]} num={3} delay={0.22} />
        </div>

        {/* Row 2 — wide right */}
        <div className="jg-row jg-r2">
          <Tile item={IMAGES[3]} num={4} delay={0.12} />
          <Tile item={IMAGES[4]} num={5} delay={0.20} />
          <Tile item={IMAGES[5]} num={6} delay={0.08} />
        </div>

        {/* Ornament */}
        <motion.div
          className="jg-orn"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 0.4 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          <div className="jg-dia" />
        </motion.div>

      </section>
    </>
  );
};

export default JewelleryGallery;